import React from 'react';
import { useIntl } from '@leagueplatform/locales';
import { usePageViewAnalytics } from '@leagueplatform/analytics';
import {
  useDocumentTitle,
  useGetUserProfile,
} from '@leagueplatform/web-common';
import {
  Box,
  Card,
  HeadingText,
  ParagraphText,
  Spinner,
  StackLayout,
  StatusBadge,
} from '@leagueplatform/genesis-core';
import { CONTENT_MAX_WIDTH } from '@leagueplatform/web-common-components';

// NOTE: This is a TEMPORARY page. It was built to fix the dead `/personal-info`
// link and to display the user's demographic profile (EOS-69). It is
// intentionally minimal and is NOT a finalized design from the EOS design or
// web teams. Fields are read-only.

const SEX_LABEL_IDS: Record<string, `STR_${string}`> = {
  female: 'STR_SEX_FEMALE',
  male: 'STR_SEX_MALE',
  intersex: 'STR_SEX_INTERSEX',
  none: 'STR_SEX_NONE',
};

const DetailRow = ({ label, value }: { label: string; value?: string }) => (
  <StackLayout
    orientation="horizontal"
    horizontalAlignment="spaceBetween"
    verticalAlignment="center"
    css={{
      width: '100%',
      paddingBlock: '$half',
      borderBottom: '1px solid $onSurfaceBorderSubdued',
    }}
  >
    <ParagraphText css={{ color: '$onSurfaceTextSubdued' }}>
      {label}
    </ParagraphText>
    <ParagraphText css={{ fontWeight: '$bold', textAlign: 'right' }}>
      {value || '—'}
    </ParagraphText>
  </StackLayout>
);

export const PersonalInfoPage = () => {
  usePageViewAnalytics({ screen_name: 'Personal info' });
  const { formatMessage } = useIntl();
  useDocumentTitle(formatMessage({ id: 'STR_PERSONAL_INFO' }));

  const { data, isLoading, error } = useGetUserProfile();
  const profile = data?.userProfile;

  const fullName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(' ');

  const sexLabelId = profile?.sex ? SEX_LABEL_IDS[profile.sex] : undefined;
  const sexLabel = sexLabelId
    ? formatMessage({ id: sexLabelId })
    : profile?.sex;

  return (
    <StackLayout
      orientation="vertical"
      horizontalAlignment="stretch"
      spacing="$two"
      css={{
        marginX: 'auto',
        padding: '$two $two $none',
        maxWidth: CONTENT_MAX_WIDTH,
        '@mobile': {
          padding: '$oneAndHalf $one $none',
        },
      }}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing="$one"
        css={{ flexWrap: 'wrap' }}
      >
        <HeadingText size="xl" level="1">
          {formatMessage({ id: 'STR_PERSONAL_INFO' })}
        </HeadingText>
        <StatusBadge
          status="warning"
          showLight
          label={formatMessage({ id: 'STR_TEMP_PAGE_BADGE' })}
        />
      </StackLayout>

      {/* Temporary-page disclaimer */}
      <Box
        css={{
          backgroundColor: '$warningBackgroundSubdued',
          border: '1px solid $warningBorderDefault',
          borderRadius: '$medium',
          padding: '$one $oneAndHalf',
        }}
      >
        <ParagraphText css={{ color: '$onSurfaceTextPrimary' }}>
          {formatMessage({ id: 'STR_TEMP_PAGE_DISCLAIMER' })}
        </ParagraphText>
      </Box>

      {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
           SDK-snapshot type quirk (unrelated to this page): useGetUserProfile's
           `isLoading` resolves to `unknown` and the genesis-core Spinner (FC)
           branch's `ReactNode` does not unify with `React.ReactNode`, so no
           precise cast satisfies tsc (Boolean() coercion still yields TS2322).
           `any` is the pragmatic unblock in this temporary page; revisit when
           the SDK react-query/genesis types align. */
        (isLoading ? (
          <StackLayout horizontalAlignment="center" css={{ padding: '$three' }}>
            <Spinner loading />
          </StackLayout>
        ) : null) as any
      }

      {!isLoading && error && (
        <ParagraphText css={{ color: '$onSurfaceTextCritical' }}>
          {formatMessage({ id: 'STR_ERROR_STATE_BODY_API' })}
        </ParagraphText>
      )}

      {!isLoading && !error && profile && (
        <StackLayout
          orientation="vertical"
          spacing="$two"
          horizontalAlignment="stretch"
        >
          <Card.Elevated>
            <Box css={{ padding: '$oneAndHalf' }}>
              <HeadingText size="md" level="2" css={{ marginBottom: '$half' }}>
                {formatMessage({ id: 'STR_PERSONAL_INFO_DETAILS' })}
              </HeadingText>
              <DetailRow
                label={formatMessage({ id: 'STR_NAME' })}
                value={fullName}
              />
              <DetailRow
                label={formatMessage({ id: 'STR_PREFERRED_NAME' })}
                value={profile.preferredFirstName}
              />
              <DetailRow
                label={formatMessage({ id: 'STR_EMAIL' })}
                value={data?.email}
              />
              <DetailRow
                label={formatMessage({ id: 'STR_SEX' })}
                value={sexLabel}
              />
              <DetailRow
                label={formatMessage({ id: 'STR_DATE_OF_BIRTH' })}
                value={profile.dateOfBirth?.slice(0, 10)}
              />
              <DetailRow
                label={formatMessage({ id: 'STR_GENDER_IDENTITY' })}
                value={profile.genderIdentity?.join(', ')}
              />
            </Box>
          </Card.Elevated>
        </StackLayout>
      )}
    </StackLayout>
  );
};
